import { BALL, uuToSim } from '../config';
import { MEDIA_KEYS, mediaAssetUrl } from '../content/MediaUrls';

export interface BallTextureSet {
  readonly baseColorUrl?: string;
  readonly normalUrl?: string;
  readonly roughnessUrl?: string;
  readonly metalnessUrl?: string;
  readonly emissiveUrl?: string;
}

export interface BallGroundIndicatorPresentation {
  readonly enabled: boolean;
  readonly color: number;
  readonly opacity: number;
  readonly floorOffset: number;
  readonly outerRadius: number;
  readonly outerThickness: number;
  readonly innerMinRadius: number;
  readonly innerMaxRadius: number;
  readonly innerThickness: number;
  readonly minHeight: number;
  readonly maxHeight: number;
  readonly heightResponseExponent: number;
  readonly segmentGapRadians: number;
}

export interface BallPresentation {
  readonly color: number;
  readonly roughness: number;
  readonly metalness: number;
  readonly emissive: number;
  readonly emissiveIntensity: number;
  readonly textures?: BallTextureSet;
  readonly groundIndicator: BallGroundIndicatorPresentation;
}

const DEFAULT_BALL_PRESENTATION: BallPresentation = Object.freeze({
  color: 0xffffff,
  roughness: 0.42,
  metalness: 0.08,
  emissive: 0x132638,
  emissiveIntensity: 0.08,
  textures: Object.freeze({
    baseColorUrl: mediaAssetUrl(MEDIA_KEYS.ballDefault),
  }),
  groundIndicator: Object.freeze({
    enabled: true,
    color: 0x182b3b,
    opacity: 0.38,
    floorOffset: uuToSim(2),
    outerRadius: BALL.radius,
    outerThickness: uuToSim(4),
    innerMinRadius: BALL.radius * 0.2,
    innerMaxRadius: BALL.radius * 0.72,
    innerThickness: uuToSim(4),
    minHeight: BALL.restingHeight,
    maxHeight: uuToSim(1100),
    heightResponseExponent: 0.62,
    segmentGapRadians: 0.16,
  }),
});

export function defaultBallPresentation(): BallPresentation {
  return DEFAULT_BALL_PRESENTATION;
}

export function validateBallPresentation(presentation: BallPresentation): void {
  validateColor(presentation.color, 'color');
  validateColor(presentation.emissive, 'emissive');
  validateUnitInterval(presentation.roughness, 'roughness');
  validateUnitInterval(presentation.metalness, 'metalness');
  if (!Number.isFinite(presentation.emissiveIntensity) || presentation.emissiveIntensity < 0) {
    throw new Error('Ball emissive intensity must be a non-negative finite number.');
  }
  for (const [name, url] of Object.entries(presentation.textures ?? {})) {
    if (typeof url !== 'string' || !url.trim()) throw new Error(`Ball texture ${name} URL must not be empty.`);
  }
  validateGroundIndicator(presentation.groundIndicator);
}

function validateGroundIndicator(indicator: BallGroundIndicatorPresentation): void {
  if (typeof indicator.enabled !== 'boolean') throw new Error('Ball ground indicator enabled must be a boolean.');
  validateColor(indicator.color, 'ground indicator color');
  validateUnitInterval(indicator.opacity, 'ground indicator opacity');
  nonNegative(indicator.floorOffset, 'ground indicator floor offset');
  positive(indicator.outerRadius, 'ground indicator outer radius');
  positive(indicator.outerThickness, 'ground indicator outer thickness');
  positive(indicator.innerMinRadius, 'ground indicator inner minimum radius');
  positive(indicator.innerMaxRadius, 'ground indicator inner maximum radius');
  positive(indicator.innerThickness, 'ground indicator inner thickness');
  nonNegative(indicator.minHeight, 'ground indicator minimum height');
  positive(indicator.maxHeight, 'ground indicator maximum height');
  positive(indicator.heightResponseExponent, 'ground indicator height response exponent');
  if (indicator.maxHeight <= indicator.minHeight) {
    throw new Error('Ball ground indicator maximum height must be greater than its minimum height.');
  }
  if (indicator.innerMinRadius > indicator.innerMaxRadius) {
    throw new Error('Ball ground indicator minimum radius must not exceed its maximum radius.');
  }
  if (indicator.innerMaxRadius >= indicator.outerRadius) {
    throw new Error('Ball ground indicator inner radius must remain smaller than its outer radius.');
  }
  if (indicator.outerThickness >= indicator.outerRadius) {
    throw new Error('Ball ground indicator outer thickness must be smaller than its radius.');
  }
  if (indicator.innerThickness >= indicator.innerMinRadius) {
    throw new Error('Ball ground indicator inner thickness must be smaller than its minimum radius.');
  }
  if (!Number.isFinite(indicator.segmentGapRadians)
    || indicator.segmentGapRadians <= 0
    || indicator.segmentGapRadians >= Math.PI / 2) {
    throw new Error('Ball ground indicator segment gap must be between 0 and PI/2 radians.');
  }
}

function validateColor(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffff) {
    throw new Error(`Ball ${label} must be an RGB integer between 0x000000 and 0xFFFFFF.`);
  }
}

function validateUnitInterval(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) {
    throw new Error(`Ball ${label} must be between 0 and 1.`);
  }
}

function positive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`Ball ${label} must be a positive finite number.`);
}

function nonNegative(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0) throw new Error(`Ball ${label} must be a non-negative finite number.`);
}
